// Copyright (c) 2021, DIALPAD INC., all rights reserved.

import React from 'react';
import {pick} from 'lodash';
import {CX_SCORE_TYPES} from './constants';
import {VeryDissatisfied} from '@app/../static/assets/icons/very-dissatisfied';
import {Dissatisfied} from '@app/../static/assets/icons/dissatisfied';
import {SomewhatDissatisfied} from '@app/../static/assets/icons/somewhat-dissatisfied';
import {Neutral} from '@app/../static/assets/icons/neutral';
import {SomewhatSatisfied} from '@app/../static/assets/icons/somewhat-satisfied';
import {Satisfied} from '@app/../static/assets/icons/satisfied';
import {VerySatisfied} from '@app/../static/assets/icons/very-satisfied';
import {
    MdRecordVoiceOver,
    MdSentimentSatisfied,
    MdShowChart,
} from 'react-icons/md';

// Renders the CES correct class & SVG Icon based on Score type.
export const renderCESIconByScoreType = (
    scoreIndex,
    iconSize,
    className = '',
    selected = false,
) => {
    const CX_SCORE_TYPE = isNaN(scoreIndex)
        ? scoreIndex
        : CX_SCORE_TYPES[Object.keys(CX_SCORE_TYPES)[scoreIndex - 1]];
    switch (CX_SCORE_TYPE) {
        case CX_SCORE_TYPES.VERY_DISSATISFIED:
            return (
                <div
                    className={`${className} danger-dark ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <VeryDissatisfied size={iconSize} />
                </div>
            );
        case CX_SCORE_TYPES.DISSATISFIED:
            return (
                <div
                    className={`${className} danger ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <Dissatisfied size={iconSize} />
                </div>
            );
        case CX_SCORE_TYPES.SOMEWHAT_DISSATISFIED:
            return (
                <div
                    className={`${className} danger-light ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <SomewhatDissatisfied size={iconSize} />
                </div>
            );
        case CX_SCORE_TYPES.NEUTRAL:
            return (
                <div
                    className={`${className} warning ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <Neutral size={iconSize} />
                </div>
            );
        case CX_SCORE_TYPES.SOMEWHAT_SATISFIED:
            return (
                <div
                    className={`${className} warning-light ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <SomewhatSatisfied size={iconSize} />
                </div>
            );
        case CX_SCORE_TYPES.SATISFIED:
            return (
                <div
                    className={`${className} success-light ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <Satisfied size={iconSize} />
                </div>
            );
        case CX_SCORE_TYPES.VERY_SATISFIED:
            return (
                <div
                    className={`${className} success ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <VerySatisfied size={iconSize} />
                </div>
            );
    }
};
// Renders the CSAT correct class & SVG Icon based on Score type.
export const renderIconByScoreType = (
    scoreIndex,
    iconSize,
    className = '',
    selected = false,
) => {
    switch (scoreIndex) {
        case -1:
            return (
                <div
                    className={`${className} danger ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <Dissatisfied size={iconSize} />
                </div>
            );
        case 0:
            return (
                <div
                    className={`${className} warning ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <Neutral size={iconSize} />
                </div>
            );
        case 1:
            return (
                <div
                    className={`${className} success ${
                        selected ? 'selected' : ''
                    }`}
                >
                    <Satisfied size={iconSize} />
                </div>
            );
    }
};

// Fetches the feedback message from the payload with the right metricId
export const fetchFeedbackMessage = (messages, metricId, parentId) => {
    return messages.find(
        (a) =>
            a.body?.metric_id === metricId &&
            a.uri === 'in/cx/feedback' &&
            a.parent_id === parentId,
    );
};

// Fetches the labels from the payload's out/cx/question with the right metricId
export const fetchCxLabels = (messages, metricId) => {
    return pick(
        messages.find(
            (a) =>
                a.event_body?.out_cx_question?.metric_id === metricId &&
                a.uri === 'out/cx/question',
        ).event_body.out_cx_question,
        ['label_negative', 'label_neutral', 'label_positive'],
    );
};

// Renders the correct title icon depending on the CX Type
export const renderTitleIconByType = (type, iconSize = 14) => {
    switch (type) {
        case 'nps':
            return <MdRecordVoiceOver size={iconSize} />;
        case 'ces':
            return <MdSentimentSatisfied size={iconSize} />;
        case 'csat':
            return <MdShowChart size={iconSize} />;
    }
};

// Renders the correct title depending on the CX Type
export const renderTitleByType = (type) => {
    switch (type) {
        case 'nps':
            return 'NET PROMOTER SCORE';
        case 'ces':
            return 'CES';
        case 'csat':
            return 'CSAT';
    }
};
